import React from 'react';
import {
  VALIDATION, VALIDATION_DURATION_REGEX, VALIDATION_PRIVATE_IPS_REGEX, VALIDATION_PRIVATE_IPS_SUBNET_REGEX,
} from 'constant';
import { _t, _tf } from 'utils';
import { v4, v6 } from 'ip-regex';
import { DurationHelpText } from 'screens/shared/form/duration-help-text';

export const validateForm = ({
  fields: _fields,
  validations: _validations,
  fieldsValues: _fieldsValues,
}) => {
  let $errors = {};

  const addError = (name, error) => {
    if (!$errors[name]) {
      $errors = { ...$errors, [name]: error };
    }
  };

  const subForms = _fields.filter(({ type = '' }) => type === 'object');
  const subFormErrors = subForms.reduce((t, c) => {
    const { name = '', fields: subFormFields = [], validations: subFormValidations = [] } = c;
    const subFormFieldValues = _fieldsValues[name] || {};

    const errors = validateForm({ fields: subFormFields, fieldsValues: subFormFieldValues, validations: subFormValidations });
    if (Object.keys(errors).length === 0) {
      return t;
    }
    return { ...t, [name]: errors };
  }, {});

  $errors = { ...$errors, ...subFormErrors };

  for (const validation of _validations) {
    const {
      name, type, max, min, message = '', regex = '',
    } = validation;
    const field = _fields.find(({ name: innerName }) => innerName === name);
    const { setVisibility } = field || { visible: false };
    let { visible = true } = field || { visible: false };
    if (setVisibility) visible = setVisibility(_fieldsValues, _fields);
    if (!visible) continue;

    const value = [null, undefined].includes(_fieldsValues[name]) ? '' : _fieldsValues[name];
    // eslint-disable-next-line prefer-destructuring
    const length = Object.keys(typeof value === 'object' ? value : value.toString()).length;
    if (type === VALIDATION.REQUIRED) {
      if (!length) {
        addError(name, message || VALIDATION.REQUIRED);
      }
    } else if (type === VALIDATION.STRING) {
      if (length !== 0 && min && length < min) {
        addError(name, _tf('MIN_STRING_VALUE', { value: min }));
      }

      if (length !== 0 && max && length > max) {
        addError(name, _tf('MAX_STRING_VALUE', { value: max }));
      }
    } else if (type === VALIDATION.VARIABLE) {
      const _regex = /^([a-zA-Z0-9-_.]+)$/;
      if (value && !_regex.test(value)) {
        addError(name, _t('COMMON.VALIDATION_TEXTS.NO_SPECIAL_CHARACTERS'));
      }
    } else if (type === VALIDATION.REGEX) {
      if (value && !regex.test(value)) {
        addError(name, message || _t('COMMON.VALIDATION_TEXTS.VALUE_NOT_VALID'));
      }
    } else if (type === VALIDATION.DURATION) {
      if (value && !VALIDATION_DURATION_REGEX.test(value)) {
        addError(name, <DurationHelpText />);
      }
    } else if (type === VALIDATION.PRIVATE_IP) {
      if (value && !value.match(VALIDATION_PRIVATE_IPS_REGEX)) {
        addError(name, _t('COMMON.VALIDATION_TEXTS.NO_VALID_PRIVATE_IP'));
      }
    } else if (type === VALIDATION.PRIVATE_IP_SUBNET) {
      if (value && !value.match(VALIDATION_PRIVATE_IPS_SUBNET_REGEX)) {
        addError(name, _t('COMMON.VALIDATION_TEXTS.NO_VALID_PRIVATE_CIDR'));
      }
    } else if (type === VALIDATION.EMAIL) {
      const emailRegex = /^([a-zA-Z0-9_\-\\.\\+]+)@([a-zA-Z0-9_\-\\.]+)\.([a-zA-Z]{2,5})$/;
      if (value && !emailRegex.test(value)) {
        addError(name, VALIDATION.EMAIL);
      }
    } else if (type === VALIDATION.IP_V4 && value) {
      if (value && !v4({ exact: true }).test(value)) {
        addError(name, _t('COMMON.VALIDATION_TEXTS.NO_VALID_IPV4'));
      }
    } else if (type === VALIDATION.NUMBER || type === VALIDATION.DECIMAL) {
      const { compareName, equals, delta = 0 } = validation;
      let compareValue = compareName && _fieldsValues[compareName];

      if (value) {
        if ((type === VALIDATION.DECIMAL && !(/^-?\d*\.?\d*$/.test(value)))
          || (type === VALIDATION.NUMBER && !(/^-?[\d]+$/.test(value)))) {
          addError(name, _t('COMMON.VALIDATION_TEXTS.NO_VALID_NUMBER'));
        }
      }

      if (compareValue) {
        compareValue = Number(compareValue) + delta; // delta value represents distance between value and compareValue
        if ((!equals && ((min && value >= compareValue) || (max && value <= compareValue)))
              || (equals && ((min && value > compareValue) || (max && value < compareValue)))) {
          addError(name, message);
        }
      } else {
        if (value < min) {
          addError(name, _tf('MIN_NUMBER_VALUE', { value: min }));
        }

        if (value > max) {
          addError(name, _tf('MAX_NUMBER_VALUE', { value: max }));
        }
      }
    } else if (type === VALIDATION.IPS_CIDR && value) {
      const [ip, cidr, other] = value.split('/');
      const ipType = v4({ exact: true }).test(ip)
        ? 4
        : v6({ exact: true }).test(ip)
          ? 6
          : 0;
      if (other || ipType === 0) {
        addError(name, _t('COMMON.VALIDATION_TEXTS.NO_VALID_IP'));
      } else if (cidr) {
        if (ipType === 4 && (cidr > 32 || cidr < 0)) {
          addError(name, _t('COMMON.VALIDATION_TEXTS.CIDR_IPV4_RANGE'));
        } else if (ipType === 6 && (cidr > 128 || cidr < 0)) {
          addError(name, _t('COMMON.VALIDATION_TEXTS.CIDR_IPV6_RANGE'));
        }
      }
    } else if (type === VALIDATION.IP4_CIDR && value) {
      const [ip, cidr, other] = value.split('/');
      const ipType = v4({ exact: true }).test(ip)
        ? 4
        : v6({ exact: true }).test(ip)
          ? 6
          : 0;
      if (other || ipType !== 4) {
        addError(name, _t('COMMON.VALIDATION_TEXTS.NO_VALID_IPV4'));
      } else if (!cidr) {
        addError(name, _t('COMMON.VALIDATION_TEXTS.CIDR_NOTATION'));
      } else if (ipType === 4 && (cidr > 32 || cidr < 0)) {
        addError(name, 'COMMON.VALIDATION_TEXTS.CIDR_IPV4_RANGE');
      }
    } else if (type === VALIDATION.ARRAY) {
      if (min) {
        if (value.length < min) {
          addError(name, message || _tf('COMMON.VALIDATION_TEXTS.MIN_ARRAY_VALUE', { value: min }));
        }
      }

      if (max) {
        if (value.length > max) {
          addError(name, message || _tf('COMMON.VALIDATION_TEXTS.MAX_ARRAY_VALUE', { value: max }));
        }
      }
    }
  }

  return $errors;
};
